import type { ShapeTextRow } from '../types/shape-text-row'
import type { ShapeTextSegment } from '../types/shape-text-segment'
import type { ShapeTextFlow } from '../types/shape-text-flow'
import type { ShapeTextChunk } from '../types/shape-text-chunk'
import type { ShapeTextCaret } from '@/lib/shapes/types/shape-text-caret'
import type { ShapeTextLayoutResult } from '../types/shape-text-layout-result'
import type { ShapeTextLayoutOptions } from '../types/shape-text-layout-options'

/** How many spaces a run may leave hanging, zero-width, against a row's right edge before the
 *  remainder wraps onto the next row. Two covers the incidental cases — the single space that
 *  separates two words at a soft wrap, and the double space after a sentence — so an ordinary
 *  wrap never indents the row it wraps onto, while a run the user typed on purpose keeps going
 *  instead of piling up invisibly at the edge. */
export const MAX_HANGING_SPACES = 2

/**
 * How far a line in front of a wrap obstacle may tighten or widen its word spaces, as a ratio of a
 * space's natural width (WMPCM-10558). Words beside an obstacle are never broken, so the line ends
 * after its last whole word and leaves up to a word's width empty before the obstacle. These close
 * part of that gap while keeping spacing visibly natural: tightening lets one more word fit, and
 * widening absorbs a little of what is left. They are InDesign's default minimum and maximum word
 * spacing. Widening further was tried and rejected for opening large, uneven gaps inside the line.
 */
export const MIN_OBSTACLE_WORD_SPACE = 0.8
export const MAX_OBSTACLE_WORD_SPACE = 1.33

export type Slot = {
  rowIndex: number
  segmentIndex: number
  x: number
}

export function nextSlot(rows: ShapeTextRow[], slot: Slot | null): Slot | null {
  if (slot == null) {
    if (rows.length === 0 || rows[0].segments.length === 0) return null
    return { rowIndex: 0, segmentIndex: 0, x: rows[0].segments[0].startX }
  }

  const row = rows[slot.rowIndex]
  const nextSegment = slot.segmentIndex + 1

  if (nextSegment < row.segments.length) {
    return {
      rowIndex: slot.rowIndex,
      segmentIndex: nextSegment,
      x: row.segments[nextSegment].startX,
    }
  }

  const nextRow = slot.rowIndex + 1

  if (nextRow >= rows.length || rows[nextRow].segments.length === 0) return null
  return { rowIndex: nextRow, segmentIndex: 0, x: rows[nextRow].segments[0].startX }
}

export function layoutShapeTextContent(
  text: string,
  flow: ShapeTextFlow,
  measureTextPt: (value: string, charIndex?: number) => number,
  paragraphAlignments?: number[],
  options?: ShapeTextLayoutOptions
): ShapeTextLayoutResult {
  const { rows } = flow
  const chunks: ShapeTextChunk[] = []
  const carets: ShapeTextCaret[] = []
  /** Index-aligned with `text` — see `ShapeTextLayoutResult.advances`. Fed by `emit`, which every
   *  placement branch routes through, so it cannot drift out of step with what was painted. */
  const advances: number[] = []
  const dropCapOverrideByIndex = options?.dropCapOverrides
    ? new Map(options.dropCapOverrides.map((o) => [o.charIndex, o.advancePt]))
    : null
  let slot: Slot | null = nextSlot(rows, null)
  let index = 0

  // Alignment tracking
  let currentParaIndex = 0
  let rowStartChunkIdx = 0
  let rowStartCaretIdx = 0
  let lastRowIdx = slot?.rowIndex ?? 0
  let lastSlotX = slot?.x ?? 0
  /** x after the current row's last NON-space glyph, or null while the row has no ink yet.
   *  Alignment measures to this rather than to `slot.x`: trailing break spaces carry no ink, so
   *  counting them shifted a centred row left by their width, and once they clamped to the row
   *  edge `usedWidth === available` left the row not aligned at all. Justify reads it for the
   *  same reason — `isGapSpace` already refuses to expand a trailing space, so the slack it
   *  distributes has to exclude them too. */
  let lastInkX: number | null = null

  /** Where the current segment's text starts in `chunks`/`carets`. Set wherever a segment begins. */
  let segmentStartChunkIdx = 0
  let segmentStartCaretIdx = 0

  /**
   * The inter-word spaces in the text placed from `chunkStartIdx` on: how many, and the narrowest
   * one's natural advance. A space after the span's last non-space character is not a gap — unless
   * `trailingAreGaps`, for a caller about to place one more word after them.
   */
  const spanGaps = (chunkStartIdx: number, trailingAreGaps = false) => {
    const spanChunks = chunks.slice(chunkStartIdx)
    const spanText = spanChunks.map((chunk) => chunk.text).join('')
    const firstGlobal = spanChunks[0]?.startCharIndex ?? 0
    let lastNonSpace = trailingAreGaps ? spanText.length : -1

    if (!trailingAreGaps) {
      for (let k = 0; k < spanText.length; k++) {
        if (spanText[k] !== ' ') lastNonSpace = k
      }
    }

    let gaps = 0
    let minGapAdvance = Infinity

    for (let k = 0; k < lastNonSpace && k < spanText.length; k++) {
      if (spanText[k] !== ' ') continue
      gaps++
      minGapAdvance = Math.min(minGapAdvance, advances[firstGlobal + k] ?? Infinity)
    }

    return { gaps, minGapAdvance }
  }

  /**
   * Moves the text placed from `chunkStartIdx` on by `perGap` at every inter-word space (negative
   * tightens), leaving its left edge (`spanStartX`) where it is. Rebuilds the chunks, splitting after
   * each gap so the text following it lands at its new x, and shifts the carets to match.
   *
   * `maxCaretX` caps where a widened caret may land: the caret after a hanging trailing space
   * would otherwise be pushed past a wrap obstacle's edge, into the obstacle.
   */
  const shiftSpanGaps = (
    chunkStartIdx: number,
    caretStartIdx: number,
    spanStartX: number,
    perGap: number,
    maxCaretX = Infinity
  ) => {
    const spanChunks = chunks.slice(chunkStartIdx)

    if (spanChunks.length === 0) return

    const spanText = spanChunks.map((chunk) => chunk.text).join('')
    const spanCharCount = spanText.length
    const spanFirstGlobal = spanChunks[0].startCharIndex
    const spanYTop = spanChunks[0].yTop
    const spanChunkHeight = spanChunks[0].rowHeight
    let lastNonSpace = -1

    for (let k = 0; k < spanCharCount; k++) {
      if (spanText[k] !== ' ') lastNonSpace = k
    }

    // Trailing spaces are not expandable gaps.
    const isGapSpace = (k: number) => spanText[k] === ' ' && k < lastNonSpace
    // shifts[k] = number of expandable gaps before character/caret boundary k.
    const shifts: number[] = new Array(spanCharCount + 1)
    let gaps = 0

    for (let k = 0; k <= spanCharCount; k++) {
      shifts[k] = gaps

      if (k < spanCharCount && isGapSpace(k)) gaps++
    }

    if (gaps === 0) return

    // Left edge of the span's k-th character: char 0 is the span start; carets carry the rest
    // (and correctly reflect any segment jumps within the span).
    const spanLeftX = (k: number) =>
      k === 0 ? spanStartX : (carets[caretStartIdx + k]?.x ?? spanStartX)
    const rebuilt: ShapeTextChunk[] = []
    let runStart = 0
    let spacesBefore = 0
    const closeRun = (endExclusive: number) => {
      if (endExclusive <= runStart) return
      rebuilt.push({
        text: spanText.slice(runStart, endExclusive),
        x: spanLeftX(runStart) + spacesBefore * perGap,
        yTop: spanYTop,
        startCharIndex: spanFirstGlobal + runStart,
        rowHeight: spanChunkHeight,
      })
    }

    for (let k = 0; k < spanCharCount; k++) {
      if (!isGapSpace(k)) continue
      closeRun(k + 1)
      spacesBefore++
      runStart = k + 1
    }

    closeRun(spanCharCount)
    chunks.splice(chunkStartIdx, spanChunks.length, ...rebuilt)

    // Shift every caret after the span-start boundary (k=0 stays put; its newRowStartX, if any,
    // is the span's fixed left edge).
    for (let k = 1; k <= spanCharCount; k++) {
      const c = carets[caretStartIdx + k]

      if (c) {
        carets[caretStartIdx + k] = { ...c, x: Math.min(c.x + shifts[k] * perGap, maxCaretX) }
      }
    }
  }

  /**
   * Text leaving a segment that ends at a wrap obstacle widens its word spaces, up to
   * `MAX_OBSTACLE_WORD_SPACE`, toward the obstacle (WMPCM-10558). Left-aligned and justified
   * paragraphs only — centred and right-aligned text is placed by `applyRowAlignment`. Never runs
   * for a paragraph's last line: a hard break starts the next segment without `advanceSlot`.
   */
  const widenTowardObstacle = (segment: ShapeTextSegment) => {
    if (!segment.endIsWrapObstacle || !hasObstacleSpacing()) return
    // `lastInkX` is row-wide; past this segment's start it is this segment's own last glyph.
    if (lastInkX == null || lastInkX <= segment.startX + 0.01) return

    const slack = segment.endX - lastInkX
    const { gaps, minGapAdvance } = spanGaps(segmentStartChunkIdx)

    if (slack <= 0.01 || gaps === 0 || !Number.isFinite(minGapAdvance)) return

    const perGap = Math.min(slack / gaps, minGapAdvance * (MAX_OBSTACLE_WORD_SPACE - 1))

    shiftSpanGaps(segmentStartChunkIdx, segmentStartCaretIdx, segment.startX, perGap, segment.endX)
    lastInkX += perGap * gaps
  }

  /** Obstacle word spacing applies to left-aligned (0) and justified (3) paragraphs. */
  const hasObstacleSpacing = () => {
    const alignment = paragraphAlignments?.[currentParaIndex] ?? 0

    return alignment === 0 || alignment === 3
  }

  const applyRowAlignment = (rowIdx: number, rowEndX: number, isWrap = false) => {
    const alignment = paragraphAlignments?.[currentParaIndex] ?? 0

    if (alignment === 0) return

    const seg = rows[rowIdx]?.segments?.[0]

    if (!seg) return

    if (alignment === 3) {
      // Justify (PA=3): only wrapped, non-terminal rows are justified — the last line of a
      // paragraph (and a row that ends because of a hard break) stays left-aligned, matching
      // yb360 and standard typography. The leftover width is distributed across inter-word
      // spaces; the row's left edge does not move.
      if (!isWrap) return

      const slack = seg.endX - rowEndX

      if (slack <= 0.01) return

      const { gaps } = spanGaps(rowStartChunkIdx)

      // No inter-word gaps (e.g. a single long word) → leave the row left-aligned.
      if (gaps === 0) return

      shiftSpanGaps(rowStartChunkIdx, rowStartCaretIdx, seg.startX, slack / gaps)

      return
    }

    const usedWidth = rowEndX - seg.startX
    const available = seg.endX - seg.startX
    const offset = alignment === 1 ? (available - usedWidth) / 2 : available - usedWidth

    if (offset <= 0) return

    for (let i = rowStartChunkIdx; i < chunks.length; i++) {
      chunks[i] = { ...chunks[i], x: chunks[i].x + offset }
    }

    const rowYTop = rows[rowIdx].yTop

    // Shift a position only when it actually sits on the row being aligned. That one rule covers
    // every case: the window's first caret carries a wrap annotation whose `nextSlotX` is *this*
    // row's left edge and does need the shift, while its own `x` belongs to the row that just ended
    // — shifting that too flung the caret for this row's first character out to the previous row's
    // end plus this row's offset, which both mis-placed the caret and mis-closed the previous row's
    // selection highlight (`computeSelectionRects` closes a row with `carets[i + 1].x`). It also
    // shifts the `nextSlotX` of a within-row segment-jump caret, whose annotation points into this
    // same row and would otherwise be left behind by the offset.
    for (let i = rowStartCaretIdx; i < carets.length; i++) {
      const c = carets[i]
      let shifted = c

      if (c.yTop === rowYTop) shifted = { ...shifted, x: shifted.x + offset }
      if (c.nextSlotX !== undefined && c.nextSlotYTop === rowYTop) {
        shifted = { ...shifted, nextSlotX: c.nextSlotX + offset }
      }

      carets[i] = shifted
    }
  }

  const pushCaret = () => {
    if (!slot) {
      const last = carets[carets.length - 1]

      // No slot and no prior caret means the flow produced zero rows — the shape is too short for
      // one line. Anchor inside the fill rather than at the bbox corner.
      carets.push(last ?? flow.fallbackCaret ?? { x: 0, yTop: 0, rowHeight: flow.rowHeight })
      return
    }

    const row = rows[slot.rowIndex]

    carets.push({ x: slot.x, yTop: row.yTop, rowHeight: flow.rowHeight })
  }

  /** Widest usable run of any row, i.e. the longest word this shape could ever hold unbroken. */
  let maxSegmentWidth = 0

  for (const row of rows) {
    for (const seg of row.segments) {
      maxSegmentWidth = Math.max(maxSegmentWidth, seg.endX - seg.startX)
    }
  }

  const isBreakSpace = (ch: string) => ch === ' ' || ch === '\t'
  /**
   * End (exclusive) of the token starting at `from`: either a run of break spaces or a run of
   * non-space characters. Break opportunities therefore sit *after* spaces, which is the same rule
   * the browser applies to the text-tool frame's `white-space: pre-wrap`.
   *
   * Always returns at least `from + 1`. Callers never pass a newline (those are handled first), but
   * the floor means the caller's `index = wordEnd` can never fail to advance and spin the loop.
   */
  const tokenEndAt = (from: number): number => {
    const spaces = isBreakSpace(text[from])
    let end = from

    while (end < text.length) {
      const ch = text[end]

      if (ch === '\n' || ch === '\r') break
      if (isBreakSpace(ch) !== spaces) break
      end++
    }

    return Math.max(end, from + 1)
  }

  /** Right edge of the chunk currently being extended, so appending needs no re-measure. */
  let openChunkEndX: number | null = null

  /**
   * Emit `value` at the cursor. Chunks are merged while they stay on the same row and remain
   * horizontally contiguous; the running `openChunkEndX` replaces the old
   * `prev.x + measureTextPt(prev.text)` comparison, which re-measured the whole chunk with the
   * frame-default style (no `charIndex`) and compared it against a sum of per-character advances —
   * two different things once kerning or a per-run size was involved.
   */
  const emit = (
    value: string,
    row: ShapeTextRow,
    x: number,
    startCharIndex: number,
    charAdvances: number[]
  ) => {
    const prev = chunks[chunks.length - 1]
    const canAppend =
      prev != null &&
      openChunkEndX != null &&
      prev.yTop === row.yTop &&
      Math.abs(openChunkEndX - x) < 0.25

    if (canAppend) prev.text += value
    else chunks.push({ text: value, x, yTop: row.yTop, startCharIndex, rowHeight: row.rowHeight })

    let width = 0

    // Written by absolute index rather than pushed: `\r` and `\n` consume an offset without
    // emitting, and they fill their own slot with 0. Keeping this array index-aligned with `text` is
    // what lets the renderer look an advance up by character index alone.
    for (let k = 0; k < charAdvances.length; k++) {
      advances[startCharIndex + k] = charAdvances[k]
      width += charAdvances[k]
    }

    openChunkEndX = x + width
  }

  /** Move to the next slot, carrying the slot-boundary alignment/caret bookkeeping. */
  const advanceSlot = () => {
    if (!slot) return

    widenTowardObstacle(rows[slot.rowIndex].segments[slot.segmentIndex])

    const oldRowIndex = slot.rowIndex
    const rowEndX = slot.x

    slot = nextSlot(rows, slot)

    if (!slot) return

    const isSameRow = slot.rowIndex === oldRowIndex

    // Annotate the boundary caret with the position the flow continues at. The caret stays where it
    // is (preserving x2 for the ending slot's selection rect) while nextSlotX/nextSlotYTop carry the
    // same offset's other position.
    //
    // This runs for a jump between two segments of one row as well as for a wrap onto the next row.
    // Skipping the same-row case left that caret parked at the previous segment's end, so no click
    // could address the offset before the first glyph of the second segment and the caret bar drew
    // itself back across the gap — a star's lower arms and a tall heart's two lobes both hit this
    // (WMPCM-8970).
    if (carets.length > 0) {
      const last = carets[carets.length - 1]

      carets[carets.length - 1] = {
        ...last,
        nextSlotX: slot.x,
        nextSlotYTop: rows[slot.rowIndex].yTop,
      }
    }

    // A segment gap is a real break in the painted run even though the row continues, so the chunk
    // must not merge across it. (`emit`'s contiguity test would otherwise be free to append when the
    // gap happens to be narrower than its 0.25 tolerance.)
    openChunkEndX = null

    // `lastSlotX` deliberately does NOT move here: it feeds the final `applyRowAlignment` as the
    // row's end x, and the row ended at the previous segment's last glyph, not at the gap.
    if (isSameRow) {
      segmentStartChunkIdx = chunks.length
      segmentStartCaretIdx = carets.length > 0 ? carets.length - 1 : carets.length
      return
    }

    // Natural line-wrap to a new row — apply alignment to the completed row. This row wrapped
    // because it ran out of width, so it is eligible for justification (isWrap = true).
    applyRowAlignment(oldRowIndex, lastInkX ?? rowEndX, true)
    rowStartChunkIdx = chunks.length
    lastInkX = null
    lastRowIdx = slot.rowIndex
    lastSlotX = slot.x
    // rowStartCaretIdx is moved back by 1 so the final applyRowAlignment shifts the boundary caret's
    // nextSlotX by the new row's centering offset as well.
    rowStartCaretIdx = carets.length > 0 ? carets.length - 1 : carets.length
    segmentStartChunkIdx = rowStartChunkIdx
    segmentStartCaretIdx = rowStartCaretIdx
  }

  pushCaret()

  while (index < text.length && slot) {
    const row = rows[slot.rowIndex]
    const segment = row.segments[slot.segmentIndex]
    const char = text[index]

    if (char === '\r') {
      // Draws nothing, but it still occupies a flat offset: skipping the caret push here left
      // `carets[i]` describing character `i + 1` for the whole rest of the frame, so every click after
      // it resolved to the wrong offset. Callers strip `\r` on the way in; this keeps the one-caret-
      // per-offset invariant true even if one slips through.
      advances[index] = 0
      index++
      pushCaret()
      continue
    }

    if (char === '\n') {
      // End of paragraph — apply alignment before jumping to the next row.
      applyRowAlignment(slot.rowIndex, lastInkX ?? slot.x)
      currentParaIndex++
      slot = nextSlot(rows, {
        rowIndex: slot.rowIndex,
        segmentIndex: row.segments.length - 1,
        x: 0,
      })
      rowStartChunkIdx = chunks.length
      rowStartCaretIdx = carets.length
      segmentStartChunkIdx = rowStartChunkIdx
      segmentStartCaretIdx = rowStartCaretIdx
      lastInkX = null
      lastRowIdx = slot?.rowIndex ?? lastRowIdx
      lastSlotX = slot?.x ?? lastSlotX
      openChunkEndX = null
      advances[index] = 0
      index++
      pushCaret()
      continue
    }

    // A drop cap's own glyph: forced advance, emitted in isolation from the word it starts — see
    // `ShapeTextLayoutOptions.dropCapOverrides`. Checked before word grouping below so the
    // remainder of the word is tokenized fresh from `index + 1`, against whatever width remains
    // after the cap's advance, exactly like any other word.
    const dropCapAdvance = dropCapOverrideByIndex?.get(index)

    if (dropCapAdvance != null) {
      emit(char, row, slot.x, index, [dropCapAdvance])
      slot = { ...slot, x: slot.x + dropCapAdvance }
      lastSlotX = slot.x
      lastRowIdx = slot.rowIndex
      index++
      pushCaret()
      continue
    }

    // Break spaces are laid out one at a time and allowed to hang at the row's edge: a trailing
    // space must never push the word after it onto the next row (`pre-wrap` semantics). Clamping
    // the advance keeps the cursor from running past the segment, so the next word sees zero
    // remaining width and wraps on its own account.
    if (isBreakSpace(char)) {
      const advance = Math.max(0.5, measureTextPt(char, index))

      // Hanging is right for the tail of a run and wrong for the body of one. Clamping every
      // space meant that once the cursor reached `segment.endX` each further space was emitted
      // with advance 0 at the identical x and an identical caret — invisible, unclickable, and
      // never able to reach the next row, because this branch returns before tokenisation and so
      // never calls `advanceSlot()`. Holding space simply froze the caret at the row's edge while
      // the characters piled up in the model and went to the wire.
      //
      // So hang only what an ordinary wrap needs to hang (see `MAX_HANGING_SPACES`) and let a
      // longer run break like anything else.
      if (
        slot.x + advance > segment.endX + 0.01 &&
        tokenEndAt(index) - index > MAX_HANGING_SPACES
      ) {
        // Another row to continue on: wrap the rest of the run onto it.
        if (nextSlot(rows, slot) != null) {
          advanceSlot()
          continue
        }

        // The end of the flow, and the same choice one row down. Clamping here was an attempt to
        // keep trailing whitespace from raising the overflow badge or crossing a chain seam, and it
        // simply moved the original bug to the last row: `consumedLength` still counted every space,
        // so nothing overflowed, nothing handed off, and a held space buried hundreds of zero-width
        // characters at the frame's edge with the caret frozen on top of them — invisible going in
        // and, because each still costs one press, invisible coming back out with Backspace.
        //
        // Stopping instead treats a space like every other character that will not fit. A chain
        // hands the rest to the next frame and the caret goes with it, which is the row-level
        // behaviour carried up to frame level; a solo frame keeps it as overset and says so with the
        // badge, exactly as it already does for an over-long word. `index` is left on the first
        // space that did not fit, so `consumedLength` reports the split point.
        break
      }

      const placedX = slot.x
      const nextX = Math.min(slot.x + advance, segment.endX)

      emit(char, row, placedX, index, [nextX - placedX])
      slot = { ...slot, x: nextX }
      lastSlotX = slot.x
      lastRowIdx = slot.rowIndex
      index++
      pushCaret()
      continue
    }

    const wordEnd = tokenEndAt(index)
    const word = text.slice(index, wordEnd)
    // Per-character advances, each measured with the style of the run that character belongs to.
    // Deliberately not one `measureTextPt(word, index)` call: `charRunMap` is per-character and the
    // editor paints a chunk as per-run spans, so a word with a style change inside it (bold applied
    // to half a word) would be measured entirely in its first character's style. Summing is also
    // exactly what the cursor consumes, which keeps the fit test, the chunk width and the caret
    // positions in agreement — and identical to what this loop produced before word wrapping.
    const wordAdvances: number[] = []
    let wordWidth = 0

    for (let i = index; i < wordEnd; i++) {
      const advance = Math.max(0.5, measureTextPt(text[i], i))

      wordAdvances.push(advance)
      wordWidth += advance
    }

    const atSegmentStart = slot.x <= segment.startX + 0.01
    const overflow = slot.x + wordWidth - segment.endX
    // In front of a wrap obstacle, a word that overflows by less than the line can give back by
    // tightening its word spaces to `MIN_OBSTACLE_WORD_SPACE` still goes on this line (WMPCM-10558).
    const tighten =
      overflow > 0.01 && segment.endIsWrapObstacle && hasObstacleSpacing()
        ? spanGaps(segmentStartChunkIdx, true)
        : null
    const tightensToFit =
      tighten != null &&
      tighten.gaps > 0 &&
      overflow <= tighten.gaps * tighten.minGapAdvance * (1 - MIN_OBSTACLE_WORD_SPACE) + 0.01

    if (overflow <= 0.01 || tightensToFit) {
      const wordStartX = slot.x

      emit(word, row, wordStartX, index, wordAdvances)

      let consumed = 0

      for (const advance of wordAdvances) {
        consumed += advance
        carets.push({ x: wordStartX + consumed, yTop: row.yTop, rowHeight: flow.rowHeight })
      }

      slot = { ...slot, x: wordStartX + wordWidth }

      if (tightensToFit) {
        shiftSpanGaps(
          segmentStartChunkIdx,
          segmentStartCaretIdx,
          segment.startX,
          -overflow / tighten.gaps
        )
        slot = { ...slot, x: segment.endX }
        // The word's chunk moved with the tightening, so text that follows can still extend it.
        openChunkEndX = segment.endX
      }

      lastSlotX = slot.x
      lastInkX = slot.x
      lastRowIdx = slot.rowIndex
      index = wordEnd
      continue
    }

    // Doesn't fit here. Defer it to the next slot — either because this row still has text on it,
    // or because this segment is too narrow to hold the word even when empty (a star's arm). Only
    // words that fit nowhere fall through to breaking, which is exactly the rule
    // `word-break: break-word` applies on the text-tool side.
    //
    // Terminating: the first branch above already covers "at a segment start and the word fits this
    // segment", so every path here either moves the cursor forward or exhausts the flow.
    const segmentCapacity = segment.endX - segment.startX
    // Deferring is only meaningful while there is somewhere left to defer TO. At the end of the flow
    // it means the whole word leaves the frame — which for a chain member is the reported bug: the
    // word visibly jumps into the next frame as the user types its last character. Falling through
    // instead lands in the per-character loop below, which places the prefix that fits and leaves
    // `index` (hence `consumedLength`) on the first character that did not.
    //
    // This also covers a shape that narrows toward the bottom: `maxSegmentWidth` is a global maximum
    // over ALL rows, so it keeps claiming the word fits somewhere while every remaining row is too
    // narrow, and the word ends up placed nowhere at all.
    const hasFurtherSlot = nextSlot(rows, slot) != null

    if (
      (hasFurtherSlot || !options?.breakTrailingWordAtCapacity) &&
      wordWidth <= maxSegmentWidth + 0.01 &&
      (!atSegmentStart || wordWidth > segmentCapacity + 0.01)
    ) {
      advanceSlot()
      continue
    }

    // Unbreakable here: wider than any segment, or a chain member has no further slot to defer to.
    // Fall back to breaking inside the word, which is what
    // `word-break: break-word` gives the text-tool frame. Progress is guaranteed by slot exhaustion,
    // not by placing a character: every iteration
    // either consumes one character or advances the slot, and `advanceSlot` walks strictly forward
    // to `null`. So a frame whose every segment is narrower than one glyph places nothing at all and
    // is reported fully overset (`placedInWord === 0` below stops the outer loop) — see the
    // no-clipped-glyph rule inside the loop for why that is the wanted outcome rather than a
    // clipped letter (WMPCM-10123).
    let placedInWord = 0

    while (index < wordEnd && slot) {
      const charRow = rows[slot.rowIndex]
      const charSegment = charRow.segments[slot.segmentIndex]
      const ch = text[index]
      const measured = Math.max(0.5, measureTextPt(ch, index))
      const fits = slot.x + measured <= charSegment.endX + 0.01

      // A glyph that does not fit here is never drawn here — it moves to the next slot, and if the
      // flow runs out of slots it stays overset (WMPCM-10123). This used to be two rules: skip when
      // a LATER segment could hold the glyph whole, but at a segment start with nothing later to
      // take it, place it anyway with its advance clamped to `charSegment.endX` and let the outline
      // `clipPath` cut the overhang. That second rule is what QA filmed as "text overlaps the Star
      // shape's border": a 100x100 star at 12pt resolves its last row to two ~7.5pt segments, one
      // per lower arm, so every `m`/`w`/`W` landing there was painted sliced by the outline, and the
      // caret — clamped to the same segment edge — was sliced with it.
      //
      // Skipping instead is not a new policy — it is the rule the two branches above this loop
      // already apply (a whole word that will not fit is deferred, a break space that will not fit
      // stops the flow), carried to the one case that had been excepted from it. `advanceSlot`
      // walks strictly forward and assigns
      // `slot = nextSlot(rows, slot)`, so exhausting the flow nulls `slot`, ends this loop, and
      // leaves `index` on the first character that did not fit — which is exactly what
      // `consumedLength` must report. That also makes the old `breakTrailingWordAtCapacity` early
      // break redundant: a chain member reaches the same split point through slot exhaustion, so the
      // remainder still crosses the seam to the next frame.
      //
      // A solo or last-in-chain frame keeps the remainder as overset and says so with the badge,
      // exactly as the break-space branch above already does for a space that will not fit. Nothing
      // is lost — the hard-block was retired in WMPCM-9338, so overset text stays in the model and
      // on the wire and reappears when the shape grows or gains a link.
      if (!fits) {
        advanceSlot()
        continue
      }

      emit(ch, charRow, slot.x, index, [measured])
      slot = { ...slot, x: slot.x + measured }
      lastSlotX = slot.x
      lastInkX = slot.x
      lastRowIdx = slot.rowIndex
      index++
      placedInWord++
      pushCaret()

      // Re-enter the outer loop once the word is consumed so the next token is measured whole.
      if (index >= wordEnd) break
    }

    // Defensive: if nothing could be placed at all (degenerate segment), stop rather than spin.
    if (placedInWord === 0) break
  }

  // Apply alignment to the final row.
  if (chunks.length > rowStartChunkIdx) {
    applyRowAlignment(lastRowIdx, lastInkX ?? lastSlotX)
  }

  while (carets.length < text.length + 1) pushCaret()

  return { chunks, carets, advances, consumedLength: index }
}
